// NavbarV2 — preview-only cream navbar matching Karine's reference mockup.
//
// LAYOUT (desktop, 1280+):
//
//   ┌──────────────────────────────────────────────────────────────┐
//   │  [search]                  KL crown                          │
//   │  Search…                  KARINE LAUZON     My Acc  [BOOK]   │
//   │                          —— RN CLINICS ——                    │
//   │                         PRECISION INJECTIONIST               │
//   ├──────────────────────────────────────────────────────────────┤
//   │  TREATMENTS ▾  PROMOTIONS ▾  ABOUT ▾  MEET THE TEAM  PRIOR   │
//   │  CARE  SHOP SKINCARE  GIFT CARDS    [CROWN RESERVE] [REFER]  │
//   └──────────────────────────────────────────────────────────────┘
//
// The KL crown plaque is centered absolutely so the search and book
// button sit symmetrically on either side. The sub-nav row is a thin
// strip flush against the bottom of the plaque.
//
// All nav items are real <Link> components — none of the destinations
// are placeholders. The plaque deep-links to "/" so tapping it goes home.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, UserRound, Gift, Crown, ChevronDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const NAV_ITEMS = (isFr) => [
  { key: "treatments", to: "/menu", label: isFr ? "Traitements" : "Treatments", caret: true },
  { key: "promotions", to: "/promotions", label: isFr ? "Promotions" : "Promotions", caret: true },
  { key: "about", to: "/about", label: isFr ? "À propos" : "About", caret: true },
  { key: "team", to: "/team", label: isFr ? "Notre équipe" : "Meet the Team" },
  { key: "care", to: "/aftercare", label: isFr ? "Soins avant & après" : "Prior Care & Appointment Care" },
  { key: "skincare", to: "/shop", label: isFr ? "Boutique" : "Shop Skincare" },
  { key: "eminence", to: "/shop/eminence", label: isFr ? "Éminence Bio" : "Eminence Organics" },
  { key: "gift", to: "/gift-cards", label: isFr ? "Cartes-cadeaux" : "Gift Cards" },
];

/* KL crown plaque — full vertical logo matching the mockup:
   small crown on top, K|L monogram in tall serif, "KARINE LAUZON" wordmark,
   "RN CLINICS" between two gold rules, and "PRECISION INJECTIONIST" subtitle.
   Built with real serif typography (Playfair) for an elegant editorial feel. */
const KLLogoPlaque = ({ size = "lg" }) => {
  const isLg = size === "lg";
  return (
    <div className="flex flex-col items-center text-center select-none">
      {/* Tiny crown */}
      <svg
        width={isLg ? 28 : 22}
        height={isLg ? 18 : 14}
        viewBox="0 0 28 18"
        fill="none"
        aria-hidden="true"
        className="mb-1"
      >
        <path
          d="M2 15 L5 5 L9 11 L14 2 L19 11 L23 5 L26 15 Z"
          fill="#C9A567"
          stroke="#C9A567"
          strokeWidth="0.7"
          strokeLinejoin="round"
        />
        <circle cx="5" cy="4" r="1.3" fill="#C9A567" />
        <circle cx="14" cy="1.5" r="1.3" fill="#C9A567" />
        <circle cx="23" cy="4" r="1.3" fill="#C9A567" />
      </svg>

      {/* KL monogram — real serif type overlapped */}
      <div
        className="relative leading-none text-zinc-900"
        style={{
          fontFamily: "'Cormorant Garamond', 'Playfair Display', serif",
          fontWeight: 500,
          fontSize: isLg ? "82px" : "60px",
          letterSpacing: "-0.18em",
          paddingRight: isLg ? "0.05em" : "0.05em",
        }}
        aria-label="KL"
      >
        <span style={{ display: "inline-block" }}>K</span>
        <span style={{ display: "inline-block" }}>L</span>
      </div>

      {/* KARINE LAUZON wordmark */}
      <div
        className={`${isLg ? "text-[34px] lg:text-[40px]" : "text-[26px]"} text-zinc-900 leading-none tracking-[0.04em] mt-3`}
        style={{ fontFamily: "'Playfair Display', 'Cormorant Garamond', serif", fontWeight: 400 }}
      >
        KARINE LAUZON
      </div>

      {/* RN CLINICS between two gold rules */}
      <div className="flex items-center gap-2 mt-2.5">
        <span className="h-px w-12 bg-[#C9A567]" />
        <span
          className="text-zinc-900 tracking-[0.42em] text-[12px] lg:text-[13px]"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
        >
          RN CLINICS
        </span>
        <span className="h-px w-12 bg-[#C9A567]" />
      </div>

      {/* PRECISION INJECTIONIST subtitle */}
      <div
        className={`${isLg ? "text-[10px] lg:text-[11px]" : "text-[9px]"} text-zinc-700 tracking-[0.36em] mt-2 uppercase`}
        style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
      >
        Precision Injectionist
      </div>
    </div>
  );
};

export default function NavbarV2({ openBookingModal }) {
  const { language } = useLanguage();
  const isFr = language === "fr";
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const q = (searchQuery || "").trim();
    if (!q) return;
    navigate(`/menu?q=${encodeURIComponent(q)}`);
  };

  const items = NAV_ITEMS(isFr);

  return (
    <nav
      className="w-full bg-[#FBF6EE] border-b border-[#E9DDC4]/70"
      data-testid="navbar-v2"
    >
      {/* TOP ROW — search · plaque · my account + book */}
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-6 pb-3">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
          {/* LEFT — search */}
          <form
            onSubmit={onSearchSubmit}
            className="flex items-center gap-2 max-w-[320px] bg-[#F1E8D4]/60 border border-[#E9DDC4] rounded-full pl-4 pr-2 py-2.5 focus-within:border-[#C9A567] transition-colors"
            data-testid="navbarv2-search"
          >
            <Search className="w-4 h-4 text-zinc-500 flex-shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={isFr ? "Rechercher des traitements…" : "Search treatments…"}
              className="flex-1 bg-transparent outline-none text-sm text-zinc-800 placeholder:text-zinc-500 min-w-0"
              data-testid="navbarv2-search-input"
            />
          </form>

          {/* CENTER — KL crown plaque (inline SVG, no asset dep) */}
          <Link
            to="/"
            className="flex flex-col items-center text-center group"
            data-testid="navbarv2-logo"
          >
            <KLLogoPlaque size="lg" />
          </Link>

          {/* RIGHT — My Account + Book */}
          <div className="flex items-center justify-end gap-3 sm:gap-5">
            <Link
              to="/account"
              className="flex items-center gap-2 text-zinc-800 hover:text-[#C9A567] transition-colors"
              data-testid="navbarv2-my-account"
            >
              <span className="w-8 h-8 rounded-full border border-zinc-400 flex items-center justify-center">
                <UserRound className="w-4 h-4" />
              </span>
              <span className="hidden sm:inline text-[13px]">{isFr ? "Mon compte" : "My Account"}</span>
            </Link>
            <button
              type="button"
              onClick={openBookingModal}
              className="px-6 py-3.5 sm:px-8 sm:py-4 rounded-full bg-gradient-to-b from-[#D4B273] via-[#C9A567] to-[#9E7A33] hover:from-[#DDBF82] hover:via-[#D1B070] hover:to-[#A88040] text-white font-bold text-[11px] sm:text-[12px] tracking-[0.22em] uppercase shadow-[0_10px_28px_-8px_rgba(160,125,56,0.6)] hover:shadow-[0_14px_36px_-8px_rgba(160,125,56,0.85)] hover:-translate-y-0.5 transition-all"
              data-testid="navbarv2-book"
            >
              {isFr ? "Réserver" : "Book Appointment"}
            </button>
          </div>
        </div>
      </div>

      {/* SUB-NAV ROW — menu items + Crown Reserve + Refer & Earn */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pb-3 sm:pb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-x-5 sm:gap-x-7 gap-y-2 flex-wrap">
            {items.map((it) => (
              <Link
                key={it.key}
                to={it.to}
                className="text-[12px] sm:text-[13px] tracking-[0.14em] uppercase text-zinc-800 hover:text-[#C9A567] transition-colors flex items-center gap-1"
                style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
                data-testid={`navbarv2-nav-${it.key}`}
              >
                {it.label}
                {it.caret && <ChevronDown className="w-3 h-3 opacity-70" />}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/rewards"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0E0B07] text-amber-300 hover:bg-zinc-800 text-[11px] tracking-[0.18em] uppercase font-bold transition-colors"
              data-testid="navbarv2-crown"
            >
              <Crown className="w-3.5 h-3.5" />
              {isFr ? "Crown Reserve" : "Crown Reserve"}
            </Link>
            <Link
              to="/refer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-transparent border border-emerald-700 text-emerald-800 hover:bg-emerald-50 text-[11px] tracking-[0.18em] uppercase font-bold transition-colors"
              data-testid="navbarv2-refer"
            >
              <Gift className="w-3.5 h-3.5" />
              {isFr ? "Parrainer & gagner 50 $" : "Refer & Earn $50"}
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}
